import { type FC } from 'react';

import {
  Box,
  Button,
  Divider,
  HStack,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { IconTrash, IconXCircle } from '@/components';

export interface CandidateDeleteBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCandidates: Array<string>;
  onDeleteCandidates: () => void;
}

export const CandidateDeleteBottomSheet: FC<
  CandidateDeleteBottomSheetProps
> = ({ isOpen, onClose, selectedCandidates, onDeleteCandidates }) => {
  const { t } = useTranslation(['common', 'components']);
  const toast = useToast();
  if (!isOpen) return null;

  return (
    <Stack
      position="fixed"
      bottom="2"
      left="0"
      right="0"
      width="fit-content"
      margin="auto"
      backdropFilter="blur(12px)"
      borderRadius="2xl"
      overflow="hidden"
      as={motion.div}
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      borderWidth={1}
      borderColor="gray.100"
      cursor="default"
      zIndex="docked"
    >
      <VStack
        h="68"
        bg="rgba(255, 255, 255, 0.24)"
        backdropFilter="blur(12px)"
        boxShadow="0px 8px 40px 0px rgba(40, 8, 80, 0.48)"
        divider={<Divider />}
        alignItems="start"
        p={2}
      >
        <HStack
          divider={
            <Divider borderColor="gray.50" h="5" orientation="vertical" />
          }
          borderRadius="2xl"
          bg="white"
          height="full"
          w="full"
          px={3}
        >
          <Text columnGap={1} fontSize="14px">
            <Text
              borderRadius="12px"
              borderWidth={1}
              borderColor="primary.200"
              bg="primary.50"
              as="span"
              px="2"
              mr={2}
            >
              {selectedCandidates?.length ?? 0}
            </Text>
            {t('common:chosen')}
          </Text>
          <Button
            variant="unstyled"
            boxShadow="none"
            alignItems="center"
            columnGap={2}
            display="flex"
            onClick={() => {
              if (selectedCandidates.length === 0) {
                return toast({
                  title: t(
                    'components:board.selectAtLeastOneCandidateToDelete'
                  ),
                  status: 'info',
                });
              }
              onDeleteCandidates();
            }}
          >
            <IconTrash fontSize="20px" color="red.400" />
            <Text fontSize="14px" color="red.400">
              {t('common:actions.deleteCandidates')}
            </Text>
          </Button>
          <Box onClick={onClose} boxShadow="none" cursor="pointer">
            <IconXCircle fontSize={20} />
          </Box>
        </HStack>
      </VStack>
    </Stack>
  );
};
